import { useState, useEffect, useRef, useCallback } from "react";
import { RefreshCw } from "lucide-react";

export interface TypewriterScenario {
  mistype?: string;
  text: string;
  results: { icon: React.ReactNode; title: string; desc: string }[];
}

interface Props {
  scenarios: TypewriterScenario[];
  title?: string;
  buttonLabel?: string;
}

export default function TypewriterDemo({ scenarios, title = "Auto-Optimization Engine", buttonLabel = "Run" }: Props) {
  const [displayText, setDisplayText] = useState("");
  const [results, setResults] = useState<TypewriterScenario["results"]>([]);
  const [visibleResults, setVisibleResults] = useState<number>(0);
  const [btnState, setBtnState] = useState<"idle" | "ready" | "working">("idle");
  const cancelRef = useRef(false);
  const mountedRef = useRef(true);

  const sleep = useCallback((ms: number) =>
    new Promise<void>(resolve => {
      const t = setTimeout(resolve, ms);
      // Check cancellation
      const check = setInterval(() => {
        if (cancelRef.current) { clearTimeout(t); clearInterval(check); resolve(); }
      }, 50);
    }), []);

  useEffect(() => {
    mountedRef.current = true;
    cancelRef.current = false;
    let idx = 0;

    async function typeText(str: string, speed = 48) {
      for (let i = 0; i < str.length; i++) {
        if (cancelRef.current) return;
        setDisplayText(prev => prev + str[i]);
        await sleep(speed + Math.random() * 25);
      }
    }

    async function delText(n: number, speed = 28) {
      for (let i = 0; i < n; i++) {
        if (cancelRef.current) return;
        setDisplayText(prev => prev.slice(0, -1));
        await sleep(speed);
      }
    }

    async function delAll(speed = 16) {
      setDisplayText(prev => {
        const len = prev.length;
        // We'll delete char by char in the loop below
        return prev;
      });
      // Get current length via a ref trick
      let len = 0;
      setDisplayText(prev => { len = prev.length; return prev; });
      await sleep(10);
      for (let i = 0; i < len; i++) {
        if (cancelRef.current) return;
        setDisplayText(prev => prev.slice(0, -1));
        await sleep(speed + Math.random() * 6);
      }
    }

    async function showResults(res: TypewriterScenario["results"]) {
      setBtnState("working");
      await sleep(500);
      setResults(res);
      for (let i = 0; i < res.length; i++) {
        if (cancelRef.current) return;
        await sleep(240);
        setVisibleResults(i + 1);
      }
      setBtnState("idle");
    }

    async function hideResults() {
      setVisibleResults(0);
      await sleep(350);
      setResults([]);
    }

    async function run() {
      while (!cancelRef.current) {
        const s = scenarios[idx % scenarios.length];
        idx++;
        setBtnState("idle");
        if (s.mistype) {
          await typeText(s.mistype, 52);
          await sleep(500);
          await delText(s.mistype.length, 24);
          await sleep(350);
        }
        if (cancelRef.current) return;
        await typeText(s.text, 46);
        setBtnState("ready");
        await sleep(700);
        await showResults(s.results);
        await sleep(5200);
        await hideResults();
        await sleep(250);
        await delAll();
        await sleep(500);
      }
    }

    const timer = setTimeout(run, 1100);

    return () => {
      cancelRef.current = true;
      mountedRef.current = false;
      clearTimeout(timer);
    };
  }, [scenarios, sleep]);

  return (
    <div className="max-w-lg mx-auto">
      <div className="bg-white rounded-2xl shadow-xl border border-black/[0.06] overflow-hidden">
        {/* Title bar */}
        <div className="flex items-center gap-2 px-4 py-3 bg-black/[0.02] border-b border-black/[0.06]">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
            <div className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
          </div>
          <span className="text-xs text-slate/50 ml-2 font-medium">{title}</span>
        </div>

        {/* Typewriter area */}
        <div className="px-5 py-4">
          <div className="flex items-start gap-3 min-h-[2.5rem]">
            <RefreshCw size={16} className="text-slate/30 mt-1 flex-shrink-0" />
            <div className="flex-1">
              <span className="text-[0.95rem] text-ink font-medium leading-relaxed">{displayText}</span>
              <span className="text-blue-500 font-light animate-pulse ml-0.5">|</span>
            </div>
          </div>
        </div>

        {/* Results area */}
        {results.length > 0 && (
          <div className="px-5 pb-3 space-y-2">
            {results.map((r, i) => (
              <div key={i}
                className={`flex items-start gap-3 p-3 bg-black/[0.02] rounded-xl transition-all duration-300 ${
                  i < visibleResults ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
                }`}>
                <div className="w-8 h-8 rounded-lg bg-white border border-black/[0.06] flex items-center justify-center flex-shrink-0 mt-0.5">
                  {r.icon}
                </div>
                <div>
                  <h4 className="text-xs font-bold text-ink">{r.title}</h4>
                  <p className="text-[11px] text-slate leading-relaxed mt-0.5">{r.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Button bar */}
        <div className="px-5 py-3 border-t border-black/[0.04] flex items-center gap-3">
          <span className="text-xs text-slate/40 flex-1">Simulating campaign performance...</span>
          <button disabled
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
              btnState === "ready" ? "bg-coral text-white opacity-100" :
              btnState === "working" ? "bg-coral/50 text-white opacity-70" :
              "bg-coral text-white opacity-30"
            }`}>
            {btnState === "working" ? "Processing..." : buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
